import type { DropdownData } from '@fitogest/reactive-form-builder/interfaces';
import type { FullMapModel } from 'src/app/models/form-common/full-map.model';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';

export type ParcelasReaReviewQueryParamMap = {
    readonly get: (name: string) => string | null;
};

export type ParcelasReaReviewInitialContext = {
    readonly queryParamMap: ParcelasReaReviewQueryParamMap;
};

export type ParcelasReaReviewInitialState = {
    readonly modelPatch: Partial<ParcelasModel> | null;
    readonly pendingFincaId: string;
};

export type ParcelasReaReviewNoticeContext = {
    readonly queryParamMap: ParcelasReaReviewQueryParamMap;
    readonly formModel: Partial<ParcelasModel> | null | undefined;
    readonly fallbackModel: Partial<ParcelasModel> | null | undefined;
};

export type ParcelasReaReviewFincaSelectionContext = {
    readonly pendingFincaId: string;
    readonly fincaDropdown: DropdownData<unknown>;
};

export type ParcelasReaReviewFincaSelection = {
    readonly fincaId: string;
    readonly selectedFinca: unknown | null;
    readonly modelPatch: Partial<ParcelasModel>;
    readonly mapPatch: Partial<FullMapModel>;
};

const REA_REVIEW_ALLOWED_FIELDS = [
    'nombre',
    'id_finca',
    'id_cliente',
    'id_usuario',
    'cod_provincia',
    'municipio',
    'agregado',
    'zona',
    'poligono',
    'numero',
    'recinto',
    'sigpac',
    'superficie_sigpac',
    'superficie_cultivada',
    'codigo_DGC',
    'activo',
    'turbo',
    'notas'
] as const;


/**
 * Encapsulates REA review query-param hydration so ParcelasFormComponent does not own URL parsing rules.
 */
export class ParcelasReaReviewAdapter {

    public static buildInitialState(context: ParcelasReaReviewInitialContext): ParcelasReaReviewInitialState {
        const modelPatch = this.getReaReviewModel(context.queryParamMap);
        const pendingFincaId = this.getReaReviewFincaId(context.queryParamMap, modelPatch);

        return {
            modelPatch,
            pendingFincaId,
        };
    }

    public static getNotice(context: ParcelasReaReviewNoticeContext): string {
        const notas = this.toStringId(context.formModel?.notas ?? context.fallbackModel?.notas);

        if (notas === '') {
            return '';
        }

        const hasReaReviewContext = context.queryParamMap.get('reaReview') === '1'
            || notas.toLowerCase().includes('rea');

        return hasReaReviewContext ? notas : '';
    }

    public static resolveFincaSelection(context: ParcelasReaReviewFincaSelectionContext): ParcelasReaReviewFincaSelection | null {
        const fincaId = context.pendingFincaId.trim();

        if (fincaId === '') {
            return null;
        }

        return {
            fincaId,
            selectedFinca: this.findDropdownValueById(context.fincaDropdown, fincaId),
            modelPatch: { id_finca: fincaId },
            mapPatch: { id_finca: fincaId },
        };
    }

    private static getReaReviewModel(queryParamMap: ParcelasReaReviewQueryParamMap): Partial<ParcelasModel> | null {
        const rawModel = queryParamMap.get('model');

        if (rawModel === null || rawModel.trim() === '') {
            return this.buildFallbackReaReviewModel(queryParamMap);
        }

        try {
            const decodedModel = JSON.parse(rawModel) as unknown;

            if (!this.isRecord(decodedModel)) {
                return this.buildFallbackReaReviewModel(queryParamMap);
            }

            const sanitizedModel = this.sanitizeReaReviewModel(decodedModel);

            return Object.keys(sanitizedModel).length > 0 ? sanitizedModel : this.buildFallbackReaReviewModel(queryParamMap);
        } catch (error: unknown) {
            console.error('Unable to read REA review parcel draft', error);

            return this.buildFallbackReaReviewModel(queryParamMap);
        }
    }

    private static buildFallbackReaReviewModel(queryParamMap: ParcelasReaReviewQueryParamMap): Partial<ParcelasModel> | null {
        const codigoDgc = queryParamMap.get('reaCodigoDgc')?.trim() ?? '';
        const idFinca = queryParamMap.get('idFinca')?.trim() ?? '';

        if (codigoDgc === '' && idFinca === '') {
            return null;
        }

        return {
            nombre: codigoDgc !== '' ? `Parcela REA ${codigoDgc}` : 'Parcela pendiente de revisión REA',
            codigo_DGC: codigoDgc,
            id_finca: idFinca,
            activo: '1',
            turbo: '1',
            notas: 'Parcela omitida en la importación REA. Revísala antes de guardarla.'
        };
    }

    private static sanitizeReaReviewModel(value: Record<string, unknown>): Partial<ParcelasModel> {
        const model: Partial<ParcelasModel> = {};

        REA_REVIEW_ALLOWED_FIELDS.forEach((field) => {
            const fieldValue = value[field];

            if (!this.isSerializablePrimitive(fieldValue)) {
                return;
            }

            model[field] = String(fieldValue);
        });

        return model;
    }

    private static getReaReviewFincaId(
        queryParamMap: ParcelasReaReviewQueryParamMap,
        model: Partial<ParcelasModel> | null,
    ): string {
        const modelFincaId = this.toStringId(model?.id_finca);

        if (modelFincaId !== '') {
            return modelFincaId;
        }

        return this.toStringId(queryParamMap.get('idFinca'));
    }

    private static findDropdownValueById(dropdown: DropdownData<unknown>, id: string): unknown | null {
        const filteredMatch = dropdown.filtered.find((item) => this.toDropdownValueId(item.value) === id);

        if (filteredMatch?.value != null) {
            return filteredMatch.value;
        }

        const valueMatch = dropdown.values.find((item) => this.toDropdownValueId(item.value) === id);

        return valueMatch?.value ?? null;
    }

    private static toDropdownValueId(value: unknown): string {
        if (!this.isRecord(value)) {
            return this.toStringId(value);
        }

        return this.toStringId(value['id'] ?? value['id_finca']);
    }

    private static isSerializablePrimitive(value: unknown): value is string | number | boolean {
        return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
    }

    private static isRecord(value: unknown): value is Record<string, unknown> {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    }

    private static toStringId(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value).trim();
    }
}
